/**
 * Performance Dashboard Component
 * Displays real-time animation performance metrics and analytics
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { usePerformanceMetrics, useAnimation } from '../lib/animations';
import { getPerformanceAnalytics, onPerformanceBudgetViolation } from '../lib/animations/performance';

interface PerformanceDashboardProps {
  isVisible: boolean;
  onClose: () => void;
  position?: 'top-right' | 'bottom-right' | 'bottom-left' | 'top-left';
}

const PerformanceDashboard: React.FC<PerformanceDashboardProps> = ({
  isVisible,
  onClose,
  position = 'top-right'
}) => {
  const metrics = usePerformanceMetrics();
  const { performanceMonitor } = useAnimation();
  const [analytics, setAnalytics] = useState(getPerformanceAnalytics());
  const [violations, setViolations] = useState<Array<{ message: string; severity: string; timestamp: number }>>([]);
  const [recommendations, setRecommendations] = useState<string[]>([]);

  useEffect(() => {
    const interval = setInterval(() => {
      setAnalytics(getPerformanceAnalytics());
      setRecommendations(performanceMonitor.getRecommendations());
    }, 1000);

    const unsubscribeViolations = onPerformanceBudgetViolation((violation, severity) => {
      setViolations(prev => [
        ...prev.slice(-4), // Keep only last 5 violations
        {
          message: violation,
          severity,
          timestamp: Date.now()
        }
      ]);
    });

    return () => {
      clearInterval(interval);
      unsubscribeViolations();
    };
  }, [performanceMonitor]);

  const getPositionClasses = () => {
    switch (position) {
      case 'top-right':
        return 'top-4 right-4';
      case 'bottom-right':
        return 'bottom-4 right-4';
      case 'bottom-left':
        return 'bottom-4 left-4';
      case 'top-left':
        return 'top-4 left-4';
      default:
        return 'top-4 right-4';
    }
  };

  const getFPSColor = (fps: number) => {
    if (fps >= 55) return 'text-green-500';
    if (fps >= 45) return 'text-yellow-500';
    return 'text-red-500';
  };

  const getMemoryColor = (memory: number) => {
    if (memory < 50) return 'text-green-500';
    if (memory < 75) return 'text-yellow-500';
    return 'text-red-500';
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'low': return 'text-blue-500';
      case 'medium': return 'text-yellow-500';
      case 'high': return 'text-red-500';
      default: return 'text-gray-500';
    }
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          className={`fixed ${getPositionClasses()} z-50 bg-black/90 backdrop-blur-sm text-white p-4 rounded-lg shadow-xl max-w-sm`}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold">Performance Monitor</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              ✕
            </button>
          </div>

          {/* Current Metrics */}
          <div className="space-y-2 mb-4">
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-300">FPS:</span>
              <span className={`text-sm font-mono ${getFPSColor(metrics.fps)}`}>
                {metrics.fps}
              </span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-300">Memory:</span>
              <span className={`text-sm font-mono ${getMemoryColor(metrics.memoryUsage)}`}>
                {metrics.memoryUsage.toFixed(1)}MB
              </span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-300">Active Animations:</span>
              <span className="text-sm font-mono text-blue-400">
                {metrics.activeAnimations}
              </span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-xs text-gray-300">Dropped Frames:</span>
              <span className="text-sm font-mono text-orange-400">
                {metrics.droppedFrames}
              </span>
            </div>
          </div>

          {/* Analytics */}
          {analytics && (
            <div className="space-y-2 mb-4 border-t border-gray-700 pt-3">
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-300">Avg FPS:</span>
                <span className="text-sm font-mono text-green-400">
                  {analytics.averageFPS.toFixed(1)}
                </span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-300">Peak Memory:</span>
                <span className="text-sm font-mono text-purple-400">
                  {analytics.peakMemoryUsage.toFixed(1)}MB
                </span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-300">Total Animations:</span>
                <span className="text-sm font-mono text-cyan-400">
                  {analytics.totalAnimations}
                </span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-300">Violations:</span>
                <span className="text-sm font-mono text-red-400">
                  {analytics.performanceViolations}
                </span>
              </div>
            </div>
          )}

          {/* Recent Violations */}
          {violations.length > 0 && (
            <div className="border-t border-gray-700 pt-3 mb-4">
              <h4 className="text-xs font-semibold text-gray-300 mb-2">Recent Issues:</h4>
              <div className="space-y-1 max-h-20 overflow-y-auto">
                {violations.slice(-3).map((violation, index) => (
                  <div key={index} className="text-xs">
                    <span className={getSeverityColor(violation.severity)}>
                      [{violation.severity.toUpperCase()}]
                    </span>
                    <span className="text-gray-300 ml-1">
                      {violation.message}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recommendations */}
          {recommendations.length > 0 && (
            <div className="border-t border-gray-700 pt-3">
              <h4 className="text-xs font-semibold text-gray-300 mb-2">Recommendations:</h4>
              <div className="space-y-1 max-h-24 overflow-y-auto">
                {recommendations.slice(0, 3).map((rec, index) => (
                  <div key={index} className="text-xs text-yellow-300">
                    • {rec}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Device Info */}
          {analytics && (
            <div className="border-t border-gray-700 pt-3 mt-4">
              <div className="text-xs text-gray-400">
                Device: {analytics.deviceInfo.cores} cores, {analytics.deviceInfo.memory}GB RAM, {analytics.deviceInfo.connection}
              </div>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PerformanceDashboard;